import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLinks } from '../hooks/useLinks';
import { useDomains } from '../hooks/useDomains';

const ModernProfile = () => {
  const { user, signOut } = useAuth();
  const { links } = useLinks();
  const { domains } = useDomains();
  const [activeTab, setActiveTab] = useState('profile');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (successMessage) setSuccessMessage('');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    // Simulate profile update
    setSuccessMessage('Profile updated successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    // Simulate password change
    setSuccessMessage('Password changed successfully!');
    setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      try {
        await signOut();
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
  };

  // Calculate user stats
  const userStats = {
    totalLinks: (links || []).length,
    totalClicks: (links || []).reduce((sum, link) => sum + (link.clickCount || 0), 0),
    activeDomains: (domains || []).filter(d => d.isVerified).length,
    memberSince: user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'
  };

  const tabs = [
    { id: 'profile', name: 'Profile Information', icon: '👤' },
    { id: 'security', name: 'Security Settings', icon: '🔒' },
    { id: 'stats', name: 'Account Statistics', icon: '📊' }
  ];

  return (
    <div className="modern-profile">
      {/* Profile Header */}
      <div className="profile-hero">
        <div className="hero-content">
          <div className="user-info">
            <div className="user-avatar">
              <span className="avatar-text">
                {(user?.firstName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
              </span>
            </div>
            <div className="user-details">
              <h1>{user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.email || 'User'}</h1>
              <p>{user?.email}</p>
              <div className="user-badges">
                <span className="role-badge">{user?.role || 'USER'}</span>
                <span className="status-badge">Active</span>
              </div>
            </div>
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span>
            Sign Out
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-banner">
          <span className="success-icon">✅</span>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="profile-tabs">
        <div className="tab-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'profile' && (
          <div className="content-section">
            <div className="section-header">
              <h3>👤 Profile Information</h3>
              <p>Update your personal information and preferences</p>
            </div>

            <form onSubmit={handleProfileUpdate} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your first name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your email"
                />
              </div>

              <button type="submit" className="submit-btn">
                💾 Save Changes
              </button>
            </form>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="content-section">
            <div className="section-header">
              <h3>🔒 Security Settings</h3>
              <p>Update your password and security preferences</p>
            </div>

            <form onSubmit={handlePasswordChange} className="security-form">
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter current password"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter new password"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <button type="submit" className="submit-btn">
                🔐 Change Password
              </button>
            </form>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="content-section">
            <div className="section-header">
              <h3>📊 Account Statistics</h3>
              <p>Overview of your account activity and usage</p>
            </div>

            <div className="stats-overview">
              <div className="overview-stats">
                <div className="overview-card">
                  <div className="overview-icon">🔗</div>
                  <div className="overview-content">
                    <div className="overview-value">{userStats.totalLinks}</div>
                    <div className="overview-label">Links Created</div>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="overview-icon">👆</div>
                  <div className="overview-content">
                    <div className="overview-value">{userStats.totalClicks}</div>
                    <div className="overview-label">Total Clicks</div>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="overview-icon">🌐</div>
                  <div className="overview-content">
                    <div className="overview-value">{userStats.activeDomains}</div>
                    <div className="overview-label">Active Domains</div>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="overview-icon">📅</div>
                  <div className="overview-content">
                    <div className="overview-value">{userStats.memberSince}</div>
                    <div className="overview-label">Member Since</div>
                  </div>
                </div>
              </div>

              <div className="account-info">
                <h4>Account Details</h4>
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-label">Account Type</span>
                    <span className="info-value">{user?.role || 'USER'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email Verified</span>
                    <span className={`info-value ${user?.emailVerified ? 'verified' : 'unverified'}`}>
                      {user?.emailVerified ? '✅ Verified' : '❌ Not Verified'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Active</span>
                    <span className="info-value">Today</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .modern-profile {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .profile-hero {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 20px;
          padding: 32px;
          margin-bottom: 24px;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .profile-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 30px 30px;
          animation: float 20s linear infinite;
        }

        @keyframes float {
          0% { transform: translate(0, 0); }
          100% { transform: translate(-30px, -30px); }
        }

        .hero-content {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .user-avatar {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .avatar-text {
          font-size: 32px;
          font-weight: 700;
          color: white;
        }

        .user-details h1 {
          margin: 0 0 4px 0;
          font-size: 24px;
          font-weight: 700;
        }

        .user-details p {
          margin: 0 0 12px 0;
          opacity: 0.8;
          font-size: 14px;
        }

        .user-badges {
          display: flex;
          gap: 8px;
        }

        .role-badge,
        .status-badge {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          padding: 4px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;
          backdrop-filter: blur(10px);
        }

        .logout-btn {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          padding: 12px 20px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          backdrop-filter: blur(10px);
        }

        .logout-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }

        .success-banner {
          background: #d1fae5;
          color: #065f46;
          padding: 12px 20px;
          border-radius: 12px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 8px;
          border: 1px solid #a7f3d0;
        }

        .profile-tabs {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          margin-bottom: 24px;
          overflow: hidden;
        }

        .tab-nav {
          display: flex;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .tab-button {
          flex: 1;
          background: none;
          border: none;
          padding: 16px 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          position: relative;
        }

        .tab-button.active {
          color: #667eea;
          background: white;
        }

        .tab-button.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #667eea;
        }

        .tab-button:hover:not(.active) {
          color: #374151;
          background: #f3f4f6;
        }

        .tab-icon {
          font-size: 16px;
        }

        .tab-content {
          padding: 0;
        }

        .content-section {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 32px;
        }

        .section-header {
          margin-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 16px;
        }

        .section-header h3 {
          margin: 0 0 8px 0;
          font-size: 20px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .section-header p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .profile-form,
        .security-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .form-input {
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          font-size: 14px;
          background: #f9fafb;
          transition: all 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .submit-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 14px 24px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          align-self: flex-start;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        .stats-overview {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .overview-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .overview-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.2s ease;
        }

        .overview-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }

        .overview-icon {
          font-size: 24px;
          width: 48px;
          height: 48px;
          background: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .overview-value {
          font-size: 24px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .overview-label {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }

        .account-info {
          background: #f9fafb;
          border-radius: 12px;
          padding: 20px;
        }

        .account-info h4 {
          margin: 0 0 16px 0;
          color: #1a1a1a;
          font-size: 16px;
          font-weight: 600;
        }

        .info-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .info-item:last-child {
          border-bottom: none;
        }

        .info-label {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }

        .info-value {
          font-size: 14px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .info-value.verified {
          color: #059669;
        }

        .info-value.unverified {
          color: #dc2626;
        }

        @media (max-width: 768px) {
          .hero-content {
            flex-direction: column;
            gap: 20px;
            text-align: center;
          }

          .tab-nav {
            flex-direction: column;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .overview-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .content-section {
            padding: 24px 20px;
          }
        }

        @media (max-width: 480px) {
          .overview-stats {
            grid-template-columns: 1fr;
          }

          .user-info {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default ModernProfile;